/**
 * Úprava obrázku podpisu pro knihovnu podpisů:
 *  – načtení z JPG/PNG nebo z první strany PDF (sken podpisu),
 *  – odstranění bílého/šedého pozadí papíru (→ průhlednost, barva inkoustu zůstává),
 *  – oříznutí na skutečný tah + malý okraj, zmenšení na rozumnou velikost.
 */
import { openPdf, renderPdfPage } from './pdfjs';

const MAX_OUT_W = 1200;

function loadImage(src: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error('Obrázek nelze načíst'));
    img.src = src;
  });
}

function readAsDataUrl(file: Blob): Promise<string> {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => resolve(r.result as string);
    r.onerror = () => reject(r.error);
    r.readAsDataURL(file);
  });
}

export function isPdfFile(file: File): boolean {
  return file.type === 'application/pdf' || /\.pdf$/i.test(file.name);
}

/** Soubor (JPG/PNG/PDF) → canvas. U PDF se bere první strana. */
export async function fileToCanvas(file: File): Promise<HTMLCanvasElement> {
  if (isPdfFile(file)) {
    const doc = await openPdf(await file.arrayBuffer());
    try {
      return await renderPdfPage(doc, 1, 3);
    } finally {
      void doc.destroy();
    }
  }
  if (!file.type.startsWith('image/')) throw new Error('Podporované jsou PDF, JPG a PNG.');
  const img = await loadImage(await readAsDataUrl(file));
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext('2d')!.drawImage(img, 0, 0);
  return c;
}

export async function dataUrlToCanvas(dataUrl: string): Promise<HTMLCanvasElement> {
  const img = await loadImage(dataUrl);
  const c = document.createElement('canvas');
  c.width = img.naturalWidth;
  c.height = img.naturalHeight;
  c.getContext('2d')!.drawImage(img, 0, 0);
  return c;
}

export interface CleanOptions {
  /** Odstranit světlé pozadí (sken/fotka). U kresleného podpisu není potřeba. */
  removeBackground?: boolean;
}

/**
 * Vyčistí podpis a vrátí PNG data URL, nebo '' když v obrázku není žádný tah.
 * Pozadí: pixely světlejší než ~88 % jsou průhledné, přechod k inkoustu je plynulý
 * (hrany tahu zůstanou vyhlazené). Barva inkoustu (modrá propiska) se zachová.
 */
export function cleanSignature(src: HTMLCanvasElement, opts: CleanOptions = {}): string {
  const { removeBackground = true } = opts;
  const w = src.width;
  const h = src.height;
  const ctx = src.getContext('2d', { willReadFrequently: true });
  if (!ctx || w === 0 || h === 0) return '';
  const img = ctx.getImageData(0, 0, w, h);
  const d = img.data;

  const HI = 225; // světlejší = pozadí
  const LO = 150; // tmavší = plný inkoust
  let minX = w, minY = h, maxX = -1, maxY = -1;

  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      const i = (y * w + x) * 4;
      let a = d[i + 3];
      if (removeBackground && a > 0) {
        const lum = 0.299 * d[i] + 0.587 * d[i + 1] + 0.114 * d[i + 2];
        const k = lum >= HI ? 0 : lum <= LO ? 1 : (HI - lum) / (HI - LO);
        a = Math.round(a * k);
        d[i + 3] = a;
      }
      if (a > 40) {
        if (x < minX) minX = x;
        if (x > maxX) maxX = x;
        if (y < minY) minY = y;
        if (y > maxY) maxY = y;
      }
    }
  }
  if (maxX < 0) return '';
  ctx.putImageData(img, 0, 0);

  const pad = Math.round(Math.max(maxX - minX, maxY - minY) * 0.04) + 2;
  const sx = Math.max(0, minX - pad);
  const sy = Math.max(0, minY - pad);
  const sw = Math.min(w, maxX + pad + 1) - sx;
  const sh = Math.min(h, maxY + pad + 1) - sy;
  const scale = Math.min(1, MAX_OUT_W / sw);

  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round(sh * scale));
  const octx = out.getContext('2d')!;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(src, sx, sy, sw, sh, 0, 0, out.width, out.height);
  return out.toDataURL('image/png');
}

/**
 * Vloží obrázek (data URL) na střed plátna w×h se zachováním poměru stran.
 * Používá SignaturePad — podpisové pole v dokumentech má pevný poměr.
 */
export async function fitDataUrl(src: string, w: number, h: number, margin = 0.06): Promise<string> {
  const img = await loadImage(src);
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d')!;
  const mw = w * (1 - margin * 2);
  const mh = h * (1 - margin * 2);
  const scale = Math.min(mw / img.naturalWidth, mh / img.naturalHeight);
  const dw = img.naturalWidth * scale;
  const dh = img.naturalHeight * scale;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(img, (w - dw) / 2, (h - dh) / 2, dw, dh);
  return c.toDataURL('image/png');
}

/**
 * Vložený podpis „ve skutečné velikosti" (knihovna / nahraný soubor) — NEvpéká
 * se do pevného poměru podpisového pole, ale uloží se oříznutý na samotný tah.
 * SignaturePad ho pak zobrazí v reálné velikosti a pole se mu přizpůsobí.
 *
 * Ořez je robustní vůči šumu skenu: hranice se berou tak, aby obsahovaly
 * 99,6 % „inkoustu" (vážené průhledností) v každé ose — osamělé tečky
 * a šedé skvrny na okraji skenu podpis nezmenší.
 *
 * Výsledný poměr stran se záměrně liší od poměru kreslicího pole (`padAspect`),
 * podle toho SignaturePad pozná vložený podpis od ručně kresleného.
 */
export async function toRealSignature(src: string, padAspect: number): Promise<string> {
  const img = await loadImage(src);
  const w = img.naturalWidth;
  const h = img.naturalHeight;
  const c = document.createElement('canvas');
  c.width = w;
  c.height = h;
  const ctx = c.getContext('2d', { willReadFrequently: true })!;
  ctx.drawImage(img, 0, 0);
  const d = ctx.getImageData(0, 0, w, h).data;

  // Mřížka 8×8 px: buňka se počítá jako inkoust, jen když v ní je aspoň
  // CELL_MIN pixelů tahu. Tečky a smítka ze skenu (pár px) tím vypadnou.
  const CELL = 8;
  const CELL_MIN = 10;
  const gw = Math.ceil(w / CELL);
  const gh = Math.ceil(h / CELL);
  const cells = new Uint16Array(gw * gh);
  for (let y = 0; y < h; y++) {
    for (let x = 0; x < w; x++) {
      if (d[(y * w + x) * 4 + 3] >= 40) cells[((y / CELL) | 0) * gw + ((x / CELL) | 0)]++;
    }
  }
  const colSum = new Float64Array(gw);
  const rowSum = new Float64Array(gh);
  let total = 0;
  for (let gy = 0; gy < gh; gy++) {
    for (let gx = 0; gx < gw; gx++) {
      const n = cells[gy * gw + gx];
      if (n < CELL_MIN) continue;
      colSum[gx] += n;
      rowSum[gy] += n;
      total += n;
    }
  }
  if (total === 0) throw new Error('V obrázku není žádný podpis.');

  const bounds = (arr: Float64Array): [number, number] => {
    const cut = total * 0.002;
    let lo = 0;
    let acc = 0;
    while (lo < arr.length - 1 && acc + arr[lo] <= cut) acc += arr[lo++];
    let hi = arr.length - 1;
    acc = 0;
    while (hi > lo && acc + arr[hi] <= cut) acc += arr[hi--];
    return [lo, hi];
  };
  const [gx0, gx1] = bounds(colSum);
  const [gy0, gy1] = bounds(rowSum);
  const x0 = gx0 * CELL;
  const x1 = Math.min(w - 1, (gx1 + 1) * CELL - 1);
  const y0 = gy0 * CELL;
  const y1 = Math.min(h - 1, (gy1 + 1) * CELL - 1);

  const pad = Math.round(Math.max(x1 - x0, y1 - y0) * 0.02) + 1;
  const sx = Math.max(0, x0 - pad);
  const sy = Math.max(0, y0 - pad);
  const sw = Math.min(w, x1 + pad + 1) - sx;
  let sh = Math.min(h, y1 + pad + 1) - sy;

  // Poměr shodný s kreslicím polem → přidej průhledný pruh, ať se rozliší.
  let extraH = 0;
  const aspect = sw / sh;
  if (Math.abs(aspect - padAspect) / padAspect < 0.06) {
    extraH = Math.ceil(sh * 0.08);
  }

  const scale = Math.min(1, 900 / sw, 500 / (sh + extraH));
  const out = document.createElement('canvas');
  out.width = Math.max(1, Math.round(sw * scale));
  out.height = Math.max(1, Math.round((sh + extraH) * scale));
  const octx = out.getContext('2d')!;
  octx.imageSmoothingQuality = 'high';
  octx.drawImage(c, sx, sy, sw, sh, 0, 0, out.width, Math.round(sh * scale));
  return out.toDataURL('image/png');
}

/** Poměr stran obrázku (šířka / výška), nebo 0 když se nepodaří načíst. */
export async function imageAspect(src: string): Promise<number> {
  try {
    const img = await loadImage(src);
    return img.naturalHeight ? img.naturalWidth / img.naturalHeight : 0;
  } catch {
    return 0;
  }
}
